import { datadogLogs } from '@datadog/browser-logs';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { z } from 'zod';
import { LanguageType, StorageKeys } from 'types/index';
import { normalizeLanguageType } from 'store/ui/i18n/normalizeLanguageType';
import { setAccessToken as storeAccessToken } from './tokenStore';
import {
	detectParentOrigin,
	isTrustedParentOrigin,
	resolveTrustedParentTargetOrigin
} from './trusted-parent-origins';
import { setUserId as storeUserId } from './userIdStore';
import { setDeviceId as storeDeviceId } from './deviceIdStore';

type CommunicationBridgeContextType = {
	send: (message: MessageToShell) => void;
	addEventListener: (callback: (message: MessageFromShell) => void) => void;
	removeEventListener: (callback: (message: MessageFromShell) => void) => void;
	isMountedInIframe: boolean;
	/**
	 * Current UI language. Owned by the shell and pushed down via `setLanguage`
	 * messages. Initialised from localStorage so the first render picks up the
	 * previously-used language before the shell has had a chance to push one.
	 */
	language: LanguageType;
};

export const CommunicationBridgeContext = createContext<CommunicationBridgeContextType | null>(
	null
);

const checkIsMountedInIframe = (): boolean => {
	try {
		return window.self !== window.top;
	} catch {
		// Cross-origin iframe will throw an error
		return false;
	}
};

// Rate-limit: SHELL_ORIGIN misconfig or untrusted parent origin causes every
// outbound send to drop. Warn once per session instead of once per message.
let hasWarnedAboutMissingParentOrigin = false;

export const CommunicationBridgeProvider = ({ children }: { children: React.ReactNode }) => {
	const isMountedInIframe = checkIsMountedInIframe();
	const [hasToken, setHasToken] = useState(false);
	const [language, setLanguage] = useState<LanguageType>(() =>
		normalizeLanguageType(localStorage.getItem(StorageKeys.UserLanguage))
	);
	const languageRef = useRef(language);
	languageRef.current = language;
	const callbacks = useRef<((message: MessageFromShell) => void)[]>([]);

	const sendMessageToParent = useCallback(
		(message: MessageToShell) => {
			const targetOrigin = resolveTrustedParentTargetOrigin();
			if (!targetOrigin) {
				if (isMountedInIframe && !hasWarnedAboutMissingParentOrigin) {
					hasWarnedAboutMissingParentOrigin = true;
					datadogLogs.logger.warn(
						'Dropping outbound postMessage — no trusted parent origin resolved',
						{ parentOrigin: detectParentOrigin() }
					);
				}
				return;
			}
			window.parent.postMessage(message, targetOrigin);
		},
		[isMountedInIframe]
	);

	// Listen for messages from the shell and notify on mount
	useEffect(() => {
		const onMessage = (e: MessageEvent) => {
			if (e.source !== window.parent) return;

			if (!isTrustedParentOrigin(e.origin)) {
				datadogLogs.logger.error('Rejected postMessage from untrusted parent origin', {
					origin: e.origin
				});
				return;
			}

			// Narrow `e.data`, not the event: excluding one message shape from
			// `MessageEvent` collapses it to `never` for the checks below, which is
			// what used to force a cast here.
			const data: unknown = e.data;

			if (isSetAccessTokenMessage(data)) {
				const token = data.accessToken;
				storeAccessToken(token);
				setHasToken(true);

				callbacks.current.forEach(callback =>
					callback({
						type: 'setAccessToken',
						accessToken: token
					})
				);
				return;
			}

			if (isSetDeviceIdMessage(data)) {
				const deviceId = data.deviceId;
				storeDeviceId(deviceId);

				callbacks.current.forEach(callback =>
					callback({
						type: 'setDeviceId',
						deviceId
					})
				);
				return;
			}

			if (isSetUserIdMessage(data)) {
				const userId = data.userId;
				storeUserId(userId);

				callbacks.current.forEach(callback =>
					callback({
						type: 'setUserId',
						userId
					})
				);
				return;
			}

			if (isSetLanguageMessage(data)) {
				const nextLanguage = normalizeLanguageType(data.language);
				if (nextLanguage === languageRef.current) return;

				localStorage.setItem(StorageKeys.UserLanguage, nextLanguage);
				setLanguage(nextLanguage);

				callbacks.current.forEach(callback =>
					callback({
						type: 'setLanguage',
						language: nextLanguage
					})
				);
				return;
			}
		};

		window.addEventListener('message', onMessage);

		sendMessageToParent({ type: 'onMount' });
		sendMessageToParent({ type: 'onResize', height: document.body.scrollHeight });

		const handleResize = () => {
			sendMessageToParent({ type: 'onResize', height: document.body.scrollHeight });
		};
		window.addEventListener('resize', handleResize, { passive: true });

		return () => {
			window.removeEventListener('message', onMessage);
			window.removeEventListener('resize', handleResize);
		};
	}, [sendMessageToParent]);

	// Activity ping: replaces InactivityDetector
	// Sends on-activity to the shell so it can refresh the Cognito session.
	// Throttled to ACTIVITY_PING_INTERVAL_MS — short enough that a 5-min
	// Cognito ID-token will get refreshed well before expiring on long-form
	// pages (e.g. the codelist import wizard), without DDoSing the shell.
	const lastSessionRefetch = useRef(Date.now());
	useEffect(() => {
		const updateLastActivityTimeStamp = () => {
			const overIntervalSinceLastRefetch =
				Date.now() - lastSessionRefetch.current > ACTIVITY_PING_INTERVAL_MS;

			if (overIntervalSinceLastRefetch) {
				lastSessionRefetch.current = Date.now();
				sendMessageToParent({ type: 'on-activity' });
			}
		};

		window.addEventListener('mousemove', updateLastActivityTimeStamp, { passive: true });
		window.addEventListener('keydown', updateLastActivityTimeStamp);
		window.addEventListener('click', updateLastActivityTimeStamp);

		return () => {
			window.removeEventListener('mousemove', updateLastActivityTimeStamp);
			window.removeEventListener('keydown', updateLastActivityTimeStamp);
			window.removeEventListener('click', updateLastActivityTimeStamp);
		};
	}, [sendMessageToParent]);

	const addEventListener = useCallback((callback: (message: MessageFromShell) => void) => {
		callbacks.current.push(callback);
	}, []);

	const removeEventListener = useCallback((callback: (message: MessageFromShell) => void) => {
		callbacks.current = callbacks.current.filter(c => c !== callback);
	}, []);

	const contextValue = useMemo<CommunicationBridgeContextType>(
		() => ({
			send: sendMessageToParent,
			addEventListener,
			removeEventListener,
			isMountedInIframe,
			language
		}),
		[sendMessageToParent, addEventListener, removeEventListener, isMountedInIframe, language]
	);

	return (
		<CommunicationBridgeContext.Provider value={contextValue}>
			{hasToken ? children : null}
		</CommunicationBridgeContext.Provider>
	);
};

// ─── Message types ───────────────────────────────────────────────────────────

/** Messages the legacy app sends UP to the shell */
export type MessageToShell =
	| { type: 'onMount' }
	| { type: 'onResize'; height: number }
	| { type: 'navigate'; path: string }
	| { type: 'replace-navigation'; path: string }
	| { type: 'on-activity' }
	| { type: 'set-title'; title: string }
	| {
			type: 'open-download-file-picker';
			url: string;
			suggestedName: string;
			downloadType:
				| 'text/csv'
				| 'application/json'
				| 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	  };

/** Messages the shell sends DOWN to the legacy app */
export type MessageFromShell =
	| {
			type: 'setAccessToken';
			accessToken: string;
	  }
	| {
			type: 'setUserId';
			userId: string;
	  }
	| {
			type: 'setDeviceId';
			deviceId: string;
	  }
	| {
			type: 'setLanguage';
			language: LanguageType;
	  };

const ACTIVITY_PING_INTERVAL_MS = 30 * 1000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const setAccessTokenMessageSchema = z.object({
	type: z.literal('setAccessToken'),
	accessToken: z.string()
});

function isSetAccessTokenMessage(
	data: unknown
): data is z.infer<typeof setAccessTokenMessageSchema> {
	return setAccessTokenMessageSchema.safeParse(data).success;
}

const setDeviceIdMessageSchema = z.object({
	type: z.literal('setDeviceId'),
	deviceId: z.string()
});

function isSetDeviceIdMessage(data: unknown): data is z.infer<typeof setDeviceIdMessageSchema> {
	return setDeviceIdMessageSchema.safeParse(data).success;
}

const setUserIdMessageSchema = z.object({
	type: z.literal('setUserId'),
	userId: z.string()
});

function isSetUserIdMessage(data: unknown): data is z.infer<typeof setUserIdMessageSchema> {
	return setUserIdMessageSchema.safeParse(data).success;
}

const setLanguageMessageSchema = z.object({
	type: z.literal('setLanguage'),
	language: z.string()
});

function isSetLanguageMessage(data: unknown): data is z.infer<typeof setLanguageMessageSchema> {
	return setLanguageMessageSchema.safeParse(data).success;
}

export const useCommunicationBridge = () => {
	const context = useContext(CommunicationBridgeContext);
	if (context === null) {
		throw new Error('useCommunicationBridge must be used within a CommunicationBridgeProvider');
	}
	return context;
};
